import { Autocomplete, Box, Button, DialogActions, DialogContent, DialogTitle, FormControl, Input, Modal, ModalDialog, Table, Typography } from "@mui/joy";
import React from "react";
import { Util } from "../Helpers/Util";
import { Mechanic } from "../Interfaces/Mechanic";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import WrappedButton from "../WrappedButton";
import WorkOrderDialog from "../Dialogs/WorkOrderDialog";
import SignatureCanvas from "react-signature-canvas";

export interface PackingList {
BuildingName: string;
InvoiceNumber: string;
SerialNumber: string;
Mechanic: string;
InvoiceHeaderID: string;
PackingListID: string;
IsDropdownOpen: boolean;
Notes: string;
SignedDate: string;
SignedDateRaw: Date | null;
TypeOfWork: string;
}

// Type for sortable columns
type SortColumn = "BuildingName" | "InvoiceNumber" | "SerialNumber" | "SignedDate";
type SortDirection = "asc" | "desc";

export default function UnapprovedPackingList() {
const [data, setData] = React.useState<Array<PackingList>>();
const [mechanics, setMechanics] = React.useState<Array<Mechanic>>();
const [signModal, setSignModal] = React.useState(false);
const [packingList, setPackingList] = React.useState<String>();
const [mechanic, setMechanic] = React.useState<String>();
const [estimate, setEstimate] = React.useState<String>();
const [buildingName, setBuildingName] = React.useState<String>();
const [pageNumber, setPageNumber] = React.useState(0);
const [isSaving, setIsSaving] = React.useState(false);
const [globalFilter, setGlobalFilter] = React.useState("");
const [openDropdown, setOpenDropdown] = React.useState<Array<boolean>>([]);
const [workOrderModal, setWorkOrderModal] = React.useState(false);
const [currentPackingListId, setCurrentPackingListId] = React.useState<string>("");
const [currentClientName, setCurrentClientName] = React.useState("");
const [currentEstimateNumber, setCurrentEstimateNumber] = React.useState("");
const [currentTypeOfWork, setCurrentTypeOfWork] = React.useState("");
const [currentNotes, setCurrentNotes] = React.useState("");
const [sortColumn, setSortColumn] = React.useState<SortColumn | null>(null);
const [sortDirection, setSortDirection] = React.useState<SortDirection>("asc");

let sigRef: any = null;

const getMechanics = () => {
fetch("User/GetPackingListAssignees", {
method: "POST",
cache: "no-cache",
headers: { "Content-Type": "application/json" },
body: JSON.stringify({}),
})
.then((x) => x.json())
.then((x) => setMechanics(x));
};

React.useEffect(() => {
getData();
getMechanics();
}, []);

const getData = () => {
sigRef && sigRef.clear();
fetch("PackingList/GetPackingLists", {
method: "Post",
cache: "no-cache",
headers: { "Content-Type": "application/json" },
body: JSON.stringify({ approved: false }),
})
.then((x) => x.json())
.then((x) => {
const arr: Array<boolean> = [];
setData(
x.map((y: any) => {
arr.push(false);
return {
BuildingName: y.building_name,
InvoiceNumber: y.invoice_number,
SerialNumber: y.serial_number,
Mechanic: y.mechanic_name,
InvoiceHeaderID: y.invoice_header_id,
PackingListID: y.packing_list_id,
IsDropdownOpen: false,
Notes: y.notes ?? "",
SignedDate: y.signed_date ? new Date(y.signed_date).toLocaleDateString() : "",
SignedDateRaw: y.signed_date ? new Date(y.signed_date) : null,
TypeOfWork: y.proposal_line_item_type ?? "",
} as PackingList;
})
);
setOpenDropdown(arr);
});
};

/**
 * Opens the work order dialog for a packing list
 */
const openWorkOrderDialog = (inv: PackingList) => {
setCurrentPackingListId(inv.PackingListID);
setCurrentClientName(inv.BuildingName);
setCurrentEstimateNumber(inv.InvoiceNumber);
setCurrentTypeOfWork(inv.TypeOfWork);
setCurrentNotes(inv.Notes);
setWorkOrderModal(true);
};

/**
 * Opens the unsigned PDF with the right side whited out
 */
const openPdf = (inv: PackingList) => {
fetch("PackingList/GetUnsignedPdf", {
method: "POST",
cache: "no-cache",
headers: { "Content-Type": "application/json" },
body: JSON.stringify({ estimateNumber: inv.InvoiceNumber }),
})
.then((x) => x.text())
.then((x) => {
var tab = window.open();
if (tab) tab.location.href = document.location.origin + "/pdfs/" + x;
});
};

/**
 * Assigns the mechanic without requiring a signature
 */
const skipSignature = () => {
setIsSaving(true);
var dt = { packingListId: packingList, mechanicId: mechanic };
fetch("PackingList/AssignPackingList", {
method: "Post",
cache: "no-cache",
headers: { "Content-Type": "application/json" },
body: JSON.stringify(dt),
}).then(() => {
setSignModal(false);
setIsSaving(false);
getData();
});
};

const save = () => {
if (sigRef.isEmpty()) {
alert("A signature is required.");
return;
}
setIsSaving(true);
var dt = {
base64Image: sigRef.toDataURL(),
packingListId: packingList,
estimateNumber: estimate,
mechanicId: mechanic,
buildingName: buildingName,
};
fetch("PackingList/SavePackingList", {
method: "Post",
cache: "no-cache",
headers: { "Content-Type": "application/json" },
body: JSON.stringify(dt),
}).then(() => {
setSignModal(false);
setIsSaving(false);
getData();
});
};

/**
 * Handles column header click for sorting
 */
const handleSort = (column: SortColumn) => {
if (sortColumn === column) {
setSortDirection(sortDirection === "asc" ? "desc" : "asc");
} else {
setSortColumn(column);
setSortDirection("asc");
}
};

/**
 * Renders sort indicator icon for column headers
 */
const renderSortIcon = (column: SortColumn) => {
if (sortColumn !== column) return null;
return sortDirection === "asc" ? (
<ArrowUpwardIcon sx={{ fontSize: 16, verticalAlign: "middle", ml: 0.5 }} />
) : (
<ArrowDownwardIcon sx={{ fontSize: 16, verticalAlign: "middle", ml: 0.5 }} />
);
};

const filteredData = data?.filter(
(x) =>
Util.isNullOrEmpty(globalFilter) ||
x.BuildingName.toLowerCase().includes(globalFilter.toLowerCase()) ||
x.InvoiceNumber.toString().includes(globalFilter)
);

// Calculate pagination values based on filtered results
const maxPages = Math.ceil((filteredData ?? []).length / 25);

// Apply sorting
const sortedData = filteredData?.sort((a, b) => {
if (!sortColumn) return 0;
let comparison = 0;
if (sortColumn === "SignedDate") {
const dateA = a.SignedDateRaw?.getTime() ?? 0;
const dateB = b.SignedDateRaw?.getTime() ?? 0;
comparison = dateA - dateB;
} else {
comparison = a[sortColumn].localeCompare(b[sortColumn]);
}
return sortDirection === "asc" ? comparison : -comparison;
});

const thisRows = sortedData?.slice(pageNumber * 25, (pageNumber + 1) * 25);

return (
<>
<WorkOrderDialog
open={workOrderModal}
packingListId={currentPackingListId}
clientName={currentClientName}
estimateNumber={currentEstimateNumber}
typeOfWork={currentTypeOfWork}
initialNotes={currentNotes}
onClose={() => setWorkOrderModal(false)}
onSaved={() => {
setWorkOrderModal(false);
getData();
}}
/>
<Modal open={signModal}>
<ModalDialog>
<DialogTitle>Signature</DialogTitle>
<DialogContent>
<SignatureCanvas
ref={(ref) => {
sigRef = ref;
console.log(sigRef);
}}
penColor="black"
canvasProps={{ width: "900", height: "400", style: { border: "1px solid black", borderRadius: "8px" } }}
/>
</DialogContent>
<DialogActions>
<WrappedButton onClick={save} busy={isSaving}>
Save Signature
</WrappedButton>
<WrappedButton onClick={skipSignature} busy={isSaving} color="warning">
Skip Signature
</WrappedButton>
<Button onClick={() => setSignModal(false)} color="primary">
Close
</Button>
</DialogActions>
</ModalDialog>
</Modal>
<Box sx={{ overflowY: "auto", padding: 2 }}>
<div style={{ display: "flex" }}>
<div style={{ flex: 1 }}>
<Typography level="title-lg">Pending Work Orders</Typography>
</div>
<FormControl sx={{ marginRight: 1 }}>
<Input
name="Search"
placeholder="Global Search"
onChange={(event) => { setGlobalFilter(event.target.value); setPageNumber(0); }}
value={globalFilter}
/>
</FormControl>
</div>
<Table stripe={"odd"} hoverRow>
<thead>
<tr>
<th style={{ cursor: "pointer" }} onClick={() => handleSort("BuildingName")}>Building Name{renderSortIcon("BuildingName")}</th>
<th style={{ cursor: "pointer" }} onClick={() => handleSort("InvoiceNumber")}>Estimate #{renderSortIcon("InvoiceNumber")}</th>
<th style={{ cursor: "pointer" }} onClick={() => handleSort("SerialNumber")}>Serial #{renderSortIcon("SerialNumber")}</th>
<th>Mechanic</th>
<th style={{ cursor: "pointer" }} onClick={() => handleSort("SignedDate")}>Signed Date{renderSortIcon("SignedDate")}</th>
<th>Open PDF</th>
<th>Work Order</th>
</tr>
</thead>
<tbody>
{thisRows?.map((x, idx) => {
return (
<tr key={idx} style={{ cursor: "pointer" }}>
<td>{x.BuildingName}</td>
<td>{x.InvoiceNumber}</td>
<td>{x.SerialNumber}</td>
<td>
<Autocomplete
value={null}
options={mechanics ?? []}
getOptionLabel={(opt) => opt.full_name}
openOnFocus
placeholder="Select Mechanic"
onChange={(event, value) => {
setSignModal(true);
setPackingList(x.PackingListID);
setMechanic(value?.users_id);
setEstimate(x.InvoiceNumber);
setBuildingName(x.BuildingName);
}}
/>
</td>
<td>{x.SignedDate}</td>
<td>
<WrappedButton onClick={() => openPdf(x)}>Open PDF</WrappedButton>
</td>
<td>
<WrappedButton onClick={() => openWorkOrderDialog(x)}>Work Order</WrappedButton>
</td>
</tr>
);
})}
</tbody>
<tfoot>
<tr>
<td colSpan={7}>
<div style={{ float: "right" }}>
<span>
<KeyboardArrowLeftIcon onClick={() => pageNumber > 0 && setPageNumber(pageNumber - 1)} sx={{ verticalAlign: "middle", cursor: "pointer" }} />
{` Page ${pageNumber + 1} of ${maxPages} `}
<KeyboardArrowRightIcon onClick={() => maxPages > pageNumber + 1 && setPageNumber(pageNumber + 1)} sx={{ verticalAlign: "middle", cursor: "pointer" }} />
</span>
</div>
</td>
</tr>
</tfoot>
</Table>
</Box>
</>
);
}
